import React, { useState } from 'react';
// Generated by GitHub Copilot
import Head from 'next/head';
import Image from 'next/image';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import superHeroData from './SuperHeroData.json';
import { sendClick } from 'reaxl-analytics';
import dynamic from 'next/dynamic';
import useAnalytics from '@kbbsdk/global-sdk/analytics/react/useAnalytics';

import {
  colors, breakpoints, FlexBox, size,
} from '@argo/principles';
import {
  PlayButtonIcon,
} from '@argo/ui-elements';
import {
  akamaiImageResize, rgba,
} from '@argo/utils';
import { akamaiLoader } from '../../../../utils';

import {
  HERO_ZINDEX, SUPER_HERO_MAX_WIDTH,
} from '../constants';

export const ctaClickTagPrefix = 'cta::hero-ad::';
export const secondaryLinkClickTagPrefix = 'link::hero-ad::';
export const secondaryLinkClickTag = 'read-more';
export const logoClickTag = 'tile::hero-ad::logo';
export const imgClickTag = 'image::hero-ad';
export const videoStartTag = 'home::video::play';
export const videoPauseTag = 'home::video::pause';
export const videoCompleteTag = 'home::video::complete';
export const videoReplayTag = 'home::video::replay';

const OverlayVideoPlayer = dynamic(() => import('@argo/base-patterns').then((module) => module.OverlayVideoPlayer));

// #region SuperHeroStyles
// below with the min width and max width is the range that the hero malfunctions
const HeroBackground = styled.div`
  background-color: ${(props) => (props.backgroundColor ? props.backgroundColor : colors.primary.offWhite)};
  position: absolute;
  left: 0;
  z-index: ${HERO_ZINDEX.HeroWrapper};
  overflow: hidden;
  width: 100%;
  height: 100%;
  @media (max-width: ${breakpoints.lg - 1}px) {
    background: ${colors.blue.marineBlue};
  }
  @media (min-width: 355px) and (max-width: 768px) {
    max-height: 100%;
    background: ${colors.blue.marineBlue};
  }
`;

const HeroImageContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  display: flex;
  justify-content: center;
  transform: translateX(-50%);
  height: 54%;
  width: 100%;
  overflow: hidden;
  /* CLS Fix: Explicit dimensions prevent layout shift */
  min-height: 233px; /* Reserve space for mobile */

  @media (min-width: ${breakpoints.lg}px) {
    max-width: ${SUPER_HERO_MAX_WIDTH}px;
    height: 505px;
    /* CLS Fix: Desktop-specific dimensions */
    min-height: 505px;
  }

  @media (min-width: 419px) and (max-width: ${breakpoints.lg - 1}px) {
    height: 52%;
  }

  @media (min-width: ${breakpoints.sm}px) and (max-width: 418px) {
    height: 42%;
  }
`;

const HeroImage = styled(Image)`
  /* Performance: Next.js Image handles optimization automatically */
  display: block;
  position: relative;
  z-index: 1; /* Place image behind content */
  
  @media (min-width: ${breakpoints.sm}px) and (max-width: ${breakpoints.lg}px) {
    object-position: center !important;
  }
`;

const HeroContentContainer = styled('div')`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2; /* Ensure content appears above the image */

  @media (min-width: ${breakpoints.lg}px) {
    background: none;
  }
`;

const SafeSpace = styled('div')`
  min-width: 310px;
  flex: 3;
  display: none;
  @media (min-width: ${breakpoints.lg}px) {
    display: block;
    max-width: 350px;
  }

  @media (min-width: ${breakpoints.xl + 1}px) {
    max-width: 535px;
  }
`;

const ContentContainer = styled('div')`
  max-height: 100%;
  margin: auto;
  flex: 1;

  @media (max-width: ${breakpoints.md}px) {
    font-size: 12px;
  }
`;

const FlexBoxContainer = styled(FlexBox)``;

const ClickableArea = styled('div')`
  height: 100%;
  width: 100%;
  cursor: pointer;
  margin-top: ${size.xs}px;
  
  .svg-play-button {
    height: ${size.xl}px;
    width: ${size.xl}px;
    margin: 0;

    @media (min-width: ${breakpoints.md}px) {
      height: ${size.xxl}px;
      width: ${size.xxl}px;
    }

    @media (min-width: ${breakpoints.max}px) {
      height: ${size.xxxl}px;
      width: ${size.xxxl}px;
      right: 54%;
      top: 1700%;
    }
  }

  &:hover {
    .svg-play-button {
      filter: drop-shadow(0 0px 20px ${rgba(colors.primary.white, 0.8)});
      :active {
        box-shadow: none;
        circle:nth-of-type(2) {
          fill: ${colors.neutral.doveGray};
        }
      }

      :focus {
        filter: drop-shadow(0 0 8px ${rgba(colors.primary.white, 0.8)});
      }
    }
  }
`;

const FlexBoxPlayButtonContainer = styled(FlexBox)`
  margin: 0;
`;

const PlayIconSubtitle = styled.p`
  color: white;
  font-family: Montserrat;
  font-size: 18px;
  font-style: normal;
  font-weight: 800;
  line-height: 24px;
  margin: 0;
`;

// #endregion SuperHeroStyles

// Generated by GitHub Copilot
export default function SHero({ isMobileDevice = false }) {
  const psxVideoStart = (event) => {
    sendClick('psxVideoStart', event);
  };

  const psxVideoComplete = (event) => {
    sendClick('psxVideoComplete', event);
  };

  const {
    contentType,
    backgroundColor = '',
    ctaText,
    image,
    wpVideoUrl = 'https://syc-np-content.s3.amazonaws.com/shared/videos/PSX_MEANT+TO+BE_HERO_V4.3.mp4',
    wpVideoThumb = 'https://syc-np-content.s3.amazonaws.com/shared/videos/PSX_MEANT+TO+BE_HERO_V4.3.mp4',
    wpVideoName = 'psx-buy-kbb-hero-video',
    backgroundContentColor = 'black',
    useTransparentCloseButton = true,
    overlayBrightness = 'dark',
    destinationUrl = '',
    destinationUrlIsExternal = false,
  } = superHeroData || {};

  const isVideo = contentType === 'video_content' && !!wpVideoUrl;
  const isEditorial = contentType === 'editorial_content';

  const [showVideoOverlay, setShowVideoOverlay] = useState(false);

  const heroImageDesktop = akamaiImageResize({ image, width: SUPER_HERO_MAX_WIDTH });

  // Ensure we have a valid URL for the image - proper path for custom Akamai loader
  const getValidImageSrc = (imageUrl) => {
    if (!imageUrl) {
      return 'psx-buy-kbb/psxHeroThumbnail.webp';
    }
    // If it's an absolute URL from another domain, return as-is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    // For relative paths used with custom akamaiLoader, remove leading slash if present
    return imageUrl.startsWith('/') ? imageUrl.substring(1) : imageUrl;
  };

  const validImageSrc = getValidImageSrc(image);

  // Small blur placeholder for better LCP
  const blurPlaceholder = [
    'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgo',
    'KCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAD/xAAhEAACAQMDBQAAAAAAAAAAAA',
    'ABAgMABAUGIWGRkbHB0eH/xAAVAQEBAAAAAAAAAAAAAAAAAAAAAf/EABQRAQAAAAAAAAAAAAAAAAAAAAD/2gAMAwEAAhEDEQA/AJdJm5iiAA',
  ].join('');

  const analytics = useAnalytics();
  const onClickGAHandler = (eventResult) => {
    analytics.push(() => ({
      type: 'click',
      googleAnalytics: {
        props: {
          event: 'analytics_event',
          eventElement: 'partner',
          eventResult,
          eventNonInteractive: true,
        },
      },
    }));
  };

  const onVideoClickGAHandler = (eventResult) => {
    analytics.push(() => ({
      type: 'click',
      googleAnalytics: {
        props: {
          event: 'analytics_event',
          eventElement: 'video',
          eventResult: `${eventResult}::${wpVideoName}`,
          eventNonInteractive: true,
          eventData: {
            video: {
              videoName: wpVideoName,
            },
          },
        },
      },
    }));
  };

  const onVideoMilestoneGAHandler = (milestone) => {
    analytics.push(() => ({
      type: 'impression',
      googleAnalytics: {
        props: {
          event: 'analytics_event',
          eventAction: 'milestone',
          eventElement: 'video',
          eventResult: `home::video::${milestone}::${wpVideoName}`,
          eventNonInteractive: true,
          eventData: {
            video: {
              videoName: wpVideoName,
            },
          },
        },
      },
    }));
  };

  const handleModuleClick = (e) => {
    e.preventDefault();

    if (isVideo) {
      psxVideoStart(e);
      return setShowVideoOverlay(true);
    }

    onClickGAHandler(imgClickTag);

    return !!destinationUrl && (typeof window !== 'undefined') && window?.open(destinationUrl, destinationUrlIsExternal ? '_blank' : '_self');
  };

  return (
    <>
      {validImageSrc && (
        // LCP Fix: Preload hero image with fetchPriority for immediate discovery
        // This ensures the LCP image is discoverable from HTML immediately
        <Head>
          <link
            rel="preload"
            as="image"
            href={validImageSrc}
            fetchPriority="high"
            imageSizes="(max-width: 768px) 100vw, (max-width: 1024px) 100vw, 1300px"
            crossOrigin="anonymous"
          />
        </Head>
      )}
      <HeroBackground
        data-automation="hero-bg"
        backgroundColor={backgroundColor}
        // CLS Fix: SSR-safe inline fallback prevents flash before Emotion loads
        className="psx-hero-bg"
      >
        <div>
          <HeroImageContainer
            data-automation="hero-image"
            // CLS Fix: Container with explicit dimensions
            className="psx-hero-image"
          >
            {/* Performance: Next.js Image optimization replaces CSS background */}
            <HeroImage
              src={validImageSrc}
              alt="Hero background"
              width={1300}
              height={505}
              priority
              fetchPriority="high"
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 100vw, 1300px"
              placeholder="blur"
              blurDataURL={blurPlaceholder}
              quality={85}
              loading="eager"
              decoding="sync"
              loader={akamaiLoader}
              onLoad={() => {
                // LCP optimization: Force paint when hero loads and mark LCP
                if (typeof window !== 'undefined') {
                  // Use scheduler.postTask for better performance if available, fallback to RAF
                  const scheduleTask = (window.scheduler && window.scheduler.postTask)
                    ? (callback) => window.scheduler.postTask(callback, { priority: 'user-blocking' })
                    : window.requestAnimationFrame;

                  scheduleTask(() => {
                    const hero = document.querySelector('[data-automation="hero-image"]');
                    if (hero) {
                      hero.style.opacity = '1';
                      // Mark as LCP candidate
                      if (window.performance && window.performance.mark) {
                        window.performance.mark('hero-image-loaded');
                      }
                    }
                  });
                }
              }}
              onError={(e) => {
                console.warn('Hero image failed to load, using fallback');
                // Fallback to a solid color background
                e.currentTarget.style.display = 'none';
              }}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center right',
                contentVisibility: 'auto',
              }}
            />
            <HeroContentContainer backgroundColor={backgroundColor}>
              <SafeSpace />
              <ContentContainer isEditorial={isEditorial}>
                <FlexBoxContainer
                  default={[0.5, 5, 3]}
                  sm={[0, 1, 0]}
                  md={[1, 1, 1]}
                  lg={[0, 2, 2]}
                  xl={[0, 2, 3]}
                  data-lean-auto="super-hero-content-container"
                >
                  <div />
                  <ClickableArea
                    className={`super-hero-clickable-${isVideo ? 'video' : 'area'}`}
                    onClick={handleModuleClick}
                    data-analytics={isVideo ? 'oem_vid_btn:button' : 'image_module:image'}
                    data-analytics-type="click"
                  >
                    {isVideo && (
                      <FlexBoxPlayButtonContainer className="play-wrapper" alignItems="center" flowColumn>
                        <PlayButtonIcon data-testid="videoPlayer-button" className="svg-play-button" size="40" />
                        <PlayIconSubtitle>See How It Works</PlayIconSubtitle>
                      </FlexBoxPlayButtonContainer>
                    )}
                  </ClickableArea>
                  <div />
                </FlexBoxContainer>
              </ContentContainer>
            </HeroContentContainer>
          </HeroImageContainer>
        </div>
        {isVideo && (
          <OverlayVideoPlayer
            id="heroVideoOverlay"
            showOverlay={showVideoOverlay}
            backgroundContentColor={backgroundContentColor}
            useTransparentCloseButton={useTransparentCloseButton}
            onModalClosed={() => setShowVideoOverlay(false)}
            videoUrl={wpVideoUrl}
            thumbnailImage={wpVideoThumb}
            autoplay={!isMobileDevice}
            maxheightRatio={75}
            widthRatio={90}
            buttonCopy={ctaText}
            buttonLink={destinationUrl}
            onRightBtnClick={() => onClickGAHandler(`${ctaClickTagPrefix}${ctaText}`)}
            // onPlay={() => onVideoClickGAHandler(videoStartTag)}
            onPlay={(item) => { console.log('this is the start ! ', item); }}
            onPause={() => onVideoClickGAHandler(videoPauseTag)}
            // onCompleted={() => onVideoClickGAHandler(videoCompleteTag)}
            onCompleted={(event) => { psxVideoComplete(event); }}
            onReplay={() => onVideoClickGAHandler(videoReplayTag)}
            onMilestone={onVideoMilestoneGAHandler}
            rightBtnAnalyticsTags={{
              'data-analytics': 'mmt_btn_desktop:button',
              'data-analytics-type': 'click',
            }}
            isExternalLink={destinationUrlIsExternal}
            overlayBrightness={overlayBrightness}
          />
        )}
      </HeroBackground>
    </>
  );
}

SHero.propTypes = {
  isMobileDevice: PropTypes.bool,
};

SHero.defaultProps = {
  isMobileDevice: false,
};
